/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useMemo } from 'react';

import { Switch } from '@coursera-web/cds-labs';

import { Card, Divider, Typography2, breakpoints } from '@coursera/cds-core';
import { BarChartThreeFillIcon, EyeIcon } from '@coursera/cds-icons';

import { useSearchExplain } from 'bundles/search-common/components/internal-search-explainer/SearchExplainContext';

import _t from 'i18n!nls/search-common';

type SearchExplainControlsProps = {
  query: string;
};

const VISIBILITY_NOTE_FONT_SIZE = 11; // px - Small text for visibility notes

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    margin-top: var(--cds-spacing-200);
    margin-bottom: var(--cds-spacing-300);
    min-width: 200px;
    width: 100%;
  `,
  row: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: var(--cds-spacing-300);
    width: 100%;
    cursor: pointer;
    user-select: none;
    margin-bottom: var(--cds-spacing-200);
  `,
  visibilityNote: css`
    display: inline-flex;
    align-items: center;
    gap: var(--cds-spacing-100);
    color: var(--cds-color-grey-600);
    font-size: ${VISIBILITY_NOTE_FONT_SIZE}px;
  `,
  visibilityNoteText: css`
    font-size: ${VISIBILITY_NOTE_FONT_SIZE}px;
  `,
  controlsRow: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-200);
  `,
  switchContainer: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-100);
    white-space: nowrap;
    flex-wrap: nowrap;

    /* Ensure Switch component stays inline */
    & > * {
      flex-shrink: 0;
    }
  `,
  switchLabelContainer: css`
    display: inline-flex;
    align-items: center;
    gap: var(--cds-spacing-100);
    white-space: nowrap;

    /* Override Switch component default alignment */
    .cds-labs-Switch {
      align-items: center !important;
      pointer-events: none; /* Disable Switch's own click handling */
      flex-direction: row !important; /* Move toggle to left side */
    }

    /* Remove default margin-bottom from Switch label */
    label {
      margin-bottom: 0 !important;
    }
  `,
  cardWrapper: css`
    display: flex;
    justify-content: center;
    width: 100%;
    overflow: hidden;
    transition: max-height 0.6s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    padding-top: 0;
    padding-bottom: 0;
  `,
  cardWrapperVisible: css`
    max-height: 2000px;
    opacity: 1;
    padding-top: var(--cds-spacing-300);
    padding-bottom: var(--cds-spacing-200);
    transition: max-height 0.6s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1),
      padding 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  `,
  cardWrapperHidden: css`
    max-height: 0;
    opacity: 0;
    padding-top: 0;
    padding-bottom: 0;
    transition: max-height 0.35s cubic-bezier(0.4, 0, 1, 1), opacity 0.25s cubic-bezier(0.4, 0, 1, 1),
      padding 0.25s cubic-bezier(0.4, 0, 1, 1);
  `,
  metricsCard: css`
    margin: 0 auto;
    padding: var(--cds-spacing-400);
    background: linear-gradient(135deg, var(--cds-color-blue-50) 0%, var(--cds-color-white-0) 100%);
    border: 2px solid var(--cds-color-blue-200);
    border-radius: 12px;
    box-shadow: 0 4px 12px var(--cds-color-darken-100);
    width: 100%;
    transform-origin: top center;
    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);

    /* Mobile: allow natural width */
    max-width: 100%;

    /* Tablet: max 2 columns width */
    ${breakpoints.up('sm')} {
      max-width: 400px;
    }

    /* Desktop: max 4 columns width */
    ${breakpoints.up('md')} {
      max-width: 800px;
    }

    .cds-Card-innerCard {
      gap: var(--cds-spacing-100);
      padding: 0;
    }
  `,
  metricsCardVisible: css`
    transform: translateY(0) scale(1);
  `,
  metricsCardHidden: css`
    transform: translateY(-8px) scale(0.98);
    transition: transform 0.25s cubic-bezier(0.4, 0, 1, 1);
  `,
  metricsCardTitle: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-200);
    padding: 0;
    padding-top: var(--cds-spacing-50);
    padding-bottom: var(--cds-spacing-50);
    margin: 0;
    border-bottom: 2px solid var(--cds-color-blue-200);

    svg {
      color: var(--cds-color-blue-600);
      flex-shrink: 0;
    }

    h3 {
      color: var(--cds-color-blue-800);
      margin: 0;
      padding: 0;
    }
  `,
  metricsCardSubtitle: css`
    color: var(--cds-color-blue-700);
    font-style: italic;
    font-size: 12px;
    margin: 0;
    padding: 0;
    line-height: 1.2;
  `,
  metricsContent: css`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: var(--cds-spacing-200);
    margin: 0;
    padding: 0;
  `,
  metricLabel: css`
    color: var(--cds-color-grey-700);
    font-weight: 500;
    font-size: 0.875rem;
  `,
  metricValue: css`
    color: var(--cds-color-blue-800);
    font-weight: 700;
    font-size: 1.125rem;
    line-height: 1.2;
    font-variant-numeric: tabular-nums;
  `,
  metricItem: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);
    padding: var(--cds-spacing-150);
    background-color: var(--cds-color-grey-50);
    border-radius: 6px;
    min-width: 0;
  `,
  metricItemFullWidth: css`
    grid-column: 1 / -1;
  `,
};

/**
 * Helper function to format metric values for display
 */
const formatMetricValue = (key: string, value: string | number): string => {
  if (typeof value === 'number') {
    if (key.toLowerCase().includes('percentage') || key.toLowerCase().includes('percentile')) {
      return `${value}%`;
    }
    if (value % 1 === 0) {
      return value.toLocaleString();
    }
    return value.toFixed(2);
  }
  return String(value);
};

/**
 * Helper function to get display names for metrics
 */
const getMetricDisplayName = (key: string): string => {
  const displayNames: Record<string, string> = {
    queryOccurrences: _t('Query Occurrences'),
    uniqueSessions: _t('Unique Sessions'),
    uniqueUsers: _t('Unique Users'),
    frequencyRank: _t('Query Rank'),
    frequencyPercentile: _t('Frequency Percentile'),
  };

  return displayNames[key] || key;
};

type Metric = {
  key: string;
  value: string | number;
  fullWidth?: boolean;
};

/**
 * Compact search explain controls for the search results header
 */
const SearchExplainControls = ({ query }: SearchExplainControlsProps) => {
  const { enableSearchExplain, toggleSearchExplain, queryMetrics, hasPermission } = useSearchExplain();
  // Arrange metrics in specific order - memoized for performance
  const arrangedMetrics = useMemo<Metric[]>(() => {
    if (!queryMetrics) return [];

    const metrics: Metric[] = [];

    // Add metrics in desired order
    if (queryMetrics.queryOccurrences !== undefined) {
      metrics.push({ key: 'queryOccurrences', value: queryMetrics.queryOccurrences });
    }
    if (queryMetrics.frequencyRank !== undefined) {
      metrics.push({ key: 'frequencyRank', value: queryMetrics.frequencyRank });
    }
    if (queryMetrics.uniqueUsers !== undefined) {
      metrics.push({ key: 'uniqueUsers', value: queryMetrics.uniqueUsers });
    }
    if (queryMetrics.uniqueSessions !== undefined) {
      metrics.push({ key: 'uniqueSessions', value: queryMetrics.uniqueSessions });
    }
    // Frequency percentile always takes full row
    if (queryMetrics.frequencyPercentile !== undefined) {
      metrics.push({ key: 'frequencyPercentile', value: queryMetrics.frequencyPercentile, fullWidth: true });
    }

    return metrics;
  }, [queryMetrics]);

  if (!hasPermission) {
    return null;
  }

  const handleToggleChange = () => {
    toggleSearchExplain();
  };

  // Render metrics card with transition
  const shouldShowCard = enableSearchExplain && queryMetrics;

  const renderMetricsCard = () => {
    return (
      <div
        css={[styles.cardWrapper, shouldShowCard ? styles.cardWrapperVisible : styles.cardWrapperHidden]}
        aria-hidden={!shouldShowCard}
      >
        <Card css={[styles.metricsCard, shouldShowCard ? styles.metricsCardVisible : styles.metricsCardHidden]}>
          <div css={styles.metricsCardTitle}>
            <BarChartThreeFillIcon size="small" />
            <Typography2 component="h3" variant="titleXSmall">
              {_t('Search Metrics for "#{query}"', { query })}
            </Typography2>
          </div>
          <Typography2 component="div" variant="bodySecondary" css={styles.metricsCardSubtitle}>
            {_t('Data from the last 28 days')}
          </Typography2>
          <div css={styles.metricsContent}>
            {arrangedMetrics.map((metric) => (
              <div key={metric.key} css={[styles.metricItem, metric.fullWidth && styles.metricItemFullWidth]}>
                <Typography2 component="span" variant="bodySecondary" css={styles.metricLabel}>
                  {getMetricDisplayName(metric.key)}
                </Typography2>
                <Typography2 component="div" variant="titleMedium" css={styles.metricValue}>
                  {formatMetricValue(metric.key, metric.value)}
                </Typography2>
              </div>
            ))}
          </div>
        </Card>
      </div>
    );
  };

  // Create label for switch
  const switchLabel = _t('Explain search results');

  return (
    <div css={styles.container}>
      <div
        css={styles.row}
        onClick={handleToggleChange}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggleChange();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={switchLabel}
      >
        <div css={styles.controlsRow}>
          <div css={styles.switchLabelContainer}>
            <Switch
              selected={enableSearchExplain}
              onToggle={handleToggleChange}
              data-testid="search-explain-toggle"
              label={switchLabel}
            />
          </div>
        </div>

        <div css={styles.visibilityNote}>
          <EyeIcon size="small" />
          <span css={styles.visibilityNoteText}>{_t('Only visible for Courserians')}</span>
        </div>
      </div>

      {renderMetricsCard()}

      <Divider />
    </div>
  );
};

export default SearchExplainControls;
